// Chain Registry - Central configuration for all supported blockchains
// To add a new chain, just add it to this registry!

import { VPS_RPC_CONFIG } from './rpcConfig';

export type ChainType = 'EVM' | 'UTXO' | 'OTHER';

export interface ChainMetadata {
  id: string;
  name: string;
  displayName: string;
  type: ChainType;
  nativeSymbol: string;
  nativeName: string;
  decimals: number;
  color: string; // Tailwind color classes
  rpcConfigKey: keyof typeof VPS_RPC_CONFIG;
  supportsTokens: boolean; // ERC-20 or equivalent
  supportsNFTs: boolean; // ERC-721 or equivalent
  enabled: boolean; // Can be toggled on/off
}

// Chain Registry - Single source of truth for all chains
export const CHAIN_REGISTRY: Record<string, ChainMetadata> = {
  ethereum: {
    id: 'ethereum',
    name: 'Ethereum',
    displayName: 'Ethereum',
    type: 'EVM',
    nativeSymbol: 'ETH',
    nativeName: 'Ethereum',
    decimals: 18,
    color: 'bg-blue-500/10 border-blue-500/30 text-blue-500',
    rpcConfigKey: 'ethereum',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  binanceSmartChain: {
    id: 'binanceSmartChain',
    name: 'Binance Smart Chain',
    displayName: 'BSC',
    type: 'EVM',
    nativeSymbol: 'BNB',
    nativeName: 'Binance Coin',
    decimals: 18,
    color: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-500',
    rpcConfigKey: 'binanceSmartChain',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  polygon: {
    id: 'polygon',
    name: 'Polygon',
    displayName: 'Polygon',
    type: 'EVM',
    nativeSymbol: 'MATIC',
    nativeName: 'Polygon',
    decimals: 18,
    color: 'bg-purple-500/10 border-purple-500/30 text-purple-500',
    rpcConfigKey: 'polygon',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  polygonZkEVM: {
    id: 'polygonZkEVM',
    name: 'Polygon zkEVM',
    displayName: 'Polygon zkEVM',
    type: 'EVM',
    nativeSymbol: 'ETH',
    nativeName: 'Ethereum',
    decimals: 18,
    color: 'bg-purple-600/10 border-purple-600/30 text-purple-600',
    rpcConfigKey: 'polygonZkEVM',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  avalanche: {
    id: 'avalanche',
    name: 'Avalanche',
    displayName: 'Avalanche',
    type: 'EVM',
    nativeSymbol: 'AVAX',
    nativeName: 'Avalanche',
    decimals: 18,
    color: 'bg-red-500/10 border-red-500/30 text-red-500',
    rpcConfigKey: 'avalanche',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  arbitrum: {
    id: 'arbitrum',
    name: 'Arbitrum',
    displayName: 'Arbitrum',
    type: 'EVM',
    nativeSymbol: 'ETH',
    nativeName: 'Ethereum',
    decimals: 18,
    color: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-500',
    rpcConfigKey: 'arbitrum',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  base: {
    id: 'base',
    name: 'Base',
    displayName: 'Base',
    type: 'EVM',
    nativeSymbol: 'ETH',
    nativeName: 'Ethereum',
    decimals: 18,
    color: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-500',
    rpcConfigKey: 'base',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  optimism: {
    id: 'optimism',
    name: 'Optimism',
    displayName: 'Optimism',
    type: 'EVM',
    nativeSymbol: 'ETH',
    nativeName: 'Ethereum',
    decimals: 18,
    color: 'bg-pink-500/10 border-pink-500/30 text-pink-500',
    rpcConfigKey: 'optimism',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  hyperliquid: {
    id: 'hyperliquid',
    name: 'Hyperliquid',
    displayName: 'Hyperliquid',
    type: 'EVM',
    nativeSymbol: 'HYPE',
    nativeName: 'Hyperliquid',
    decimals: 18,
    color: 'bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/30 text-blue-500',
    rpcConfigKey: 'hyperliquid',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  fantom: {
    id: 'fantom',
    name: 'Fantom',
    displayName: 'Fantom',
    type: 'EVM',
    nativeSymbol: 'FTM',
    nativeName: 'Fantom',
    decimals: 18,
    color: 'bg-blue-400/10 border-blue-400/30 text-blue-400',
    rpcConfigKey: 'fantom',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  zkSyncEra: {
    id: 'zkSyncEra',
    name: 'zkSync Era',
    displayName: 'zkSync Era',
    type: 'EVM',
    nativeSymbol: 'ETH',
    nativeName: 'Ethereum',
    decimals: 18,
    color: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500',
    rpcConfigKey: 'zkSyncEra',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  linea: {
    id: 'linea',
    name: 'Linea',
    displayName: 'Linea',
    type: 'EVM',
    nativeSymbol: 'ETH',
    nativeName: 'Ethereum',
    decimals: 18,
    color: 'bg-teal-500/10 border-teal-500/30 text-teal-500',
    rpcConfigKey: 'linea',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  scroll: {
    id: 'scroll',
    name: 'Scroll',
    displayName: 'Scroll',
    type: 'EVM',
    nativeSymbol: 'ETH',
    nativeName: 'Ethereum',
    decimals: 18,
    color: 'bg-slate-500/10 border-slate-500/30 text-slate-500',
    rpcConfigKey: 'scroll',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  mantle: {
    id: 'mantle',
    name: 'Mantle',
    displayName: 'Mantle',
    type: 'EVM',
    nativeSymbol: 'MNT',
    nativeName: 'Mantle',
    decimals: 18,
    color: 'bg-green-500/10 border-green-500/30 text-green-500',
    rpcConfigKey: 'mantle',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  blast: {
    id: 'blast',
    name: 'Blast',
    displayName: 'Blast',
    type: 'EVM',
    nativeSymbol: 'ETH',
    nativeName: 'Ethereum',
    decimals: 18,
    color: 'bg-yellow-400/10 border-yellow-400/30 text-yellow-400',
    rpcConfigKey: 'blast',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  celo: {
    id: 'celo',
    name: 'Celo',
    displayName: 'Celo',
    type: 'EVM',
    nativeSymbol: 'CELO',
    nativeName: 'Celo',
    decimals: 18,
    color: 'bg-amber-500/10 border-amber-500/30 text-amber-500',
    rpcConfigKey: 'celo',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  gnosis: {
    id: 'gnosis',
    name: 'Gnosis Chain',
    displayName: 'Gnosis',
    type: 'EVM',
    nativeSymbol: 'xDAI',
    nativeName: 'Gnosis',
    decimals: 18,
    color: 'bg-green-600/10 border-green-600/30 text-green-600',
    rpcConfigKey: 'gnosis',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  mode: {
    id: 'mode',
    name: 'Mode Network',
    displayName: 'Mode',
    type: 'EVM',
    nativeSymbol: 'ETH',
    nativeName: 'Ethereum',
    decimals: 18,
    color: 'bg-violet-500/10 border-violet-500/30 text-violet-500',
    rpcConfigKey: 'mode',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  solana: {
    id: 'solana',
    name: 'Solana',
    displayName: 'Solana',
    type: 'OTHER',
    nativeSymbol: 'SOL',
    nativeName: 'Solana',
    decimals: 9,
    color: 'bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/30 text-purple-500',
    rpcConfigKey: 'solana',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  ravencoin: {
    id: 'ravencoin',
    name: 'Ravencoin',
    displayName: 'Ravencoin',
    type: 'UTXO',
    nativeSymbol: 'RVN',
    nativeName: 'Ravencoin',
    decimals: 8,
    color: 'bg-purple-500/10 border-purple-500/30 text-purple-500',
    rpcConfigKey: 'ravencoin',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
  icp: {
    id: 'icp',
    name: 'Internet Computer',
    displayName: 'Internet Computer',
    type: 'OTHER',
    nativeSymbol: 'ICP',
    nativeName: 'Internet Computer',
    decimals: 8,
    color: 'bg-gradient-to-r from-orange-500/10 to-pink-500/10 border-orange-500/30 text-orange-500',
    rpcConfigKey: 'icp',
    supportsTokens: true,
    supportsNFTs: true,
    enabled: true,
  },
};

// Dynamic ChainId type based on registry
export type ChainId = keyof typeof CHAIN_REGISTRY;

// Get all enabled chains
export function getEnabledChains(): ChainMetadata[] {
  return Object.values(CHAIN_REGISTRY).filter(chain => chain.enabled);
}

// Get chain metadata by ID
export function getChainMetadata(chainId: ChainId): ChainMetadata | undefined {
  return CHAIN_REGISTRY[chainId];
}

// Get all EVM chains
export function getEVMChains(): ChainMetadata[] {
  return Object.values(CHAIN_REGISTRY).filter(
    chain => chain.enabled && chain.type === 'EVM'
  );
}

// Get chains that support tokens
export function getTokenSupportedChains(): ChainMetadata[] {
  return Object.values(CHAIN_REGISTRY).filter(
    chain => chain.enabled && chain.supportsTokens
  );
}

// Get chains that support NFTs
export function getNFTSupportedChains(): ChainMetadata[] {
  return Object.values(CHAIN_REGISTRY).filter(
    chain => chain.enabled && chain.supportsNFTs
  );
}

// Get RPC URLs for a chain (dynamic lookup)
export function getChainRPCUrls(chainId: ChainId): [string, string] {
  const chain = CHAIN_REGISTRY[chainId];
  if (!chain) {
    return ['', ''];
  }

  const config = VPS_RPC_CONFIG[chain.rpcConfigKey];
  if (!config) {
    return ['', ''];
  }

  // Handle primary/fallback structure
  if ('primary' in config && 'fallback' in config) {
    return [config.primary || '', config.fallback || ''];
  }

  // Handle direct url structure (mm2, ravencoin)
  if ('url' in config) {
    return [config.url || '', ''];
  }

  return ['', ''];
}

// Get RPC URL for a chain (with fallback)
export function getChainRPCUrl(chainId: ChainId): string {
  const [primary, fallback] = getChainRPCUrls(chainId);
  return primary || fallback;
}

// Map chain registry IDs to numeric chainIds (for EVM chains)
// Used by aggregators and wallet connections
export function getNumericChainId(chainId: ChainId): number | null {
  const chainIdMap: Record<string, number> = {
    ethereum: 1,
    binanceSmartChain: 56,
    polygon: 137,
    polygonZkEVM: 1101,
    avalanche: 43114,
    arbitrum: 42161,
    base: 8453,
    optimism: 10,
    hyperliquid: 998,
    fantom: 250,
    zkSyncEra: 324,
    linea: 59144,
    scroll: 534352,
    mantle: 5000,
    blast: 81457,
    celo: 42220,
    gnosis: 100,
    mode: 34443,
    // UTXO chains don't have numeric chainIds
    ravencoin: 0, // Special case - not an EVM chain
    solana: 0, // Special case - not an EVM chain
  };
  
  return chainIdMap[chainId] ?? null;
}

// Get numeric chainId for EVM chains only
export function getEVMChainId(chainId: ChainId): number | null {
  const chain = CHAIN_REGISTRY[chainId];
  if (!chain || chain.type !== 'EVM') {
    return null;
  }
  return getNumericChainId(chainId);
}

// Check if a chain is EVM-compatible (for aggregator routing)
export function isEVMChain(chainId: ChainId): boolean {
  const chain = CHAIN_REGISTRY[chainId];
  return chain?.type === 'EVM' || false;
}



